import React, { useMemo } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TicketMessageChannel } from 'flow/types/Tickets';
import { selectTicketsAllowedMimeTypesData } from 'flow/selectors/TicketsSelectors';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    channel?: TicketMessageChannel;
    mimeType: string;
}

const AllowedFileExtensions = ({ channel, mimeType }: Props) => {
    const { i18next } = useAppContext();
    const allowedMimeTypesData = useAppSelector((state) =>
        selectTicketsAllowedMimeTypesData(state, channel),
    );
    const mimeTypePrefix = mimeType?.split('/')?.[0];
    const filterString =
        mimeTypePrefix === 'image' ||
        mimeTypePrefix === 'video' ||
        mimeTypePrefix === 'audio'
            ? mimeTypePrefix
            : undefined;
    // About this component in general, and filterString specifically:
    // For images, videos, and audio files, we display a title specific for that media type,
    // and only list allowed file extensions for that specific media type, i.e.:
    // "Supported image formats: .jpg, .png, [...etc]"
    //
    // Other types of files, such as 'application', might not make a lot of sense for the user,
    // why we in that case display a more generic phrase, and list all allowed file extensions, i.e.:
    // "Formats supported: .docx, .jpg, .pdf, .png, [...etc]"

    const allowedFileExtensions = useMemo(() => {
        const mimeTypes: any =
            allowedMimeTypesData
                .keySeq()
                .filter((mimeType: any) =>
                    filterString ? mimeType.startsWith(mimeTypePrefix) : true,
                )
                ?.toArray() || [];
        return getFileExtensionsForMimeTypes(mimeTypes);
    }, [allowedMimeTypesData, mimeTypePrefix, filterString]);

    return (
        <div className="flex flex-col gap-2">
            <span className="font-bold">
                {i18next.t(geti18KeySupportedFormatsTitle(mimeType))}
            </span>
            <ul className="scrollbar max-h-52 list-disc overflow-y-auto pl-5">
                {allowedFileExtensions.map((format: string, index: number) =>
                    format ? (
                        <li key={`afm-${format}-${index}`}>{format}</li>
                    ) : null,
                )}
            </ul>
        </div>
    );
};

export default AllowedFileExtensions;

const getFileExtensionsForMimeTypes = (mimeTypes: string[]) => {
    if (!mimeTypes) return [];
    return (
        Array.from(
            new Set(
                mimeTypes?.flatMap((mimeType) =>
                    getFileExtensionsForMimeType(mimeType)?.split(', '),
                ),
            ),
        )?.sort((a, b) => a.localeCompare(b)) || []
    );
};

const getFileExtensionsForMimeType = (mimeType?: string) => {
    if (!mimeType) return '';
    switch (true) {
        case mimeType === 'application/msword':
            return '.doc';
        case mimeType === 'application/pdf':
            return '.pdf';
        case mimeType === 'application/vnd.ms-excel':
            return '.xls';
        case mimeType === 'application/vnd.ms-powerpoint':
            return '.ppt';
        case mimeType ===
            'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            return '.pptx';
        case mimeType ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return '.xlsx';
        case mimeType === 'audio/aac':
            return '.aac';
        case mimeType === 'audio/amr':
            return '.amr';
        case mimeType === 'audio/mp4':
            return '.m4a';
        case mimeType === 'audio/mpeg':
            return '.mp3';
        case mimeType === 'audio/ogg':
            return '.ogg';
        case mimeType === 'image/jpeg':
            return '.jpg, .jpeg';
        case mimeType === 'image/png':
            return '.png';
        case mimeType === 'image/webp':
            return '.webp';
        case mimeType === 'text/plain':
            return '.txt';
        case mimeType === 'video/3gpp':
            return '.3gp';
        case mimeType === 'video/mp4':
            return '.mp4';
        default:
            return '';
    }
};

const geti18KeySupportedFormatsTitle = (mimeType: string) => {
    if (mimeType?.startsWith('audio'))
        return 'tickets.input.attachment.formats_supported.audio';
    if (mimeType?.startsWith('image'))
        return 'tickets.input.attachment.formats_supported.image';
    if (mimeType?.startsWith('video'))
        return 'tickets.input.attachment.formats_supported.video';
    return 'tickets.input.attachment.formats_supported.generic';
};
